import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  collaborateListQuery,
  collaborateShellQuery,
  createCollaborateTemplate,
  removeCollaborateTemplate,
  updateCollaborateTemplate,
  type CollaborateTemplate,
} from '../../api/settingsEaseedeskMessageTemplates'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Template messages (sabloanele de colaborare).
 *
 * Element cu element fata de `version-3/easeedesk_settings/comments/
 * comment_templates/comment_templates.php` + `templates_list.php`, si gest cu
 * gest fata de `comment_templates-1.js` si `templates_list-1.js`.
 *
 * Previzualizarea arata textul ca TEXT. In portal se punea prin `data-message`
 * si se scria in pagina asa cum era.
 */

/**
 * Randul de sub titlu, ca in macheta: `strlen($mesaj) > 100 ?
 * substr(strip_tags($mesaj), 0, 100) . '...' : strip_tags($mesaj)`.
 *
 * Se TAIE ETICHETELE. Sabloanele au fost scrise cu editorul din portal, deci
 * corpul lor e HTML; fara taiere, randul din lista incepea cu `<p>` si numara
 * cele 100 de caractere in `<strong>`-uri, nu in cuvinte. Masura se ia pe
 * textul brut, ca in portal - una din ciudateniile lui, pastrata.
 */
function excerpt(body: string): string {
  const text = body.replace(/<[^>]*>/g, '')
  return body.length > 100 ? `${text.slice(0, 100)}...` : text
}

function TemplateModal({
  template,
  open,
  onClose,
  onSaved,
  companies,
}: {
  template?: CollaborateTemplate
  open: boolean
  onClose(): void
  onSaved(): void
  companies: Array<{ ID: number; name: string }>
}) {
  const editing = template !== undefined
  const [company, setCompany] = useState(0)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')

  useEffect(() => {
    if (!open) return
    setCompany(template?.company_id ?? 0)
    setTitle(template?.title ?? '')
    setBody(template?.message ?? '')
  }, [open, template])

  const save = useMutation({
    mutationFn: () =>
      editing
        ? updateCollaborateTemplate(template.id, company, title, body)
        : createCollaborateTemplate(company, title, body),
    onSuccess: () => {
      toast(editing ? 'Template updated.' : 'Template created.', 'Template messages')
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editMessageModal' : 'newMessagesModal'}
      labelledBy={editing ? 'editMessageModalLabel' : 'newMessagesModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit template' : 'New template'}
    >
      <form
        id={editing ? 'edit-message-form' : 'new-message-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (title.trim() === '' || body.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label className="control-label">
            Company <span className="required">*</span>
          </label>
          <select
            className="form-control input-sm"
            name="company"
            value={company}
            onChange={(e) => setCompany(Number(e.target.value))}
          >
            <option value={0}>All companies</option>
            {companies.map((row) => (
              <option key={row.ID} value={row.ID}>
                {row.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="control-label">Title</label>
          <input
            type="text"
            name="title"
            className="template-body form-control"
            maxLength={255}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="control-label">Message body</label>
          <textarea
            name="message_body"
            className="template-body form-control"
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
          />
        </div>
        <hr className="dotted" />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-field"
            disabled={title.trim() === '' || body.trim() === '' || save.isPending}
          >
            {editing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function CollaborateTemplatesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(collaborateShellQuery())
  const [company, setCompany] = useState(0)
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<CollaborateTemplate | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  const list = useQuery(collaborateListQuery(company))

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'collaborate_template_messages'] })
  }

  const remove = useMutation({
    mutationFn: (id: number) => removeCollaborateTemplate(id),
    onSuccess: () => {
      toast('Template removed.', 'Template messages')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Template messages" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  const templates = list.data?.templates ?? []

  return (
    <div>
      <h4>
        <strong>Template messages</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage collaborate <strong>message template</strong> settings.
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create new template
      </button>
      <hr />

      <div className="row" style={{ marginBottom: 20 }}>
        <div className="col-md-12">
          <form className="form-inline" id="filter_language_source" onSubmit={(e) => e.preventDefault()}>
            <div className="form-group col-md-3">
              <label>Company</label>
              <select
                className="form-control input-sm"
                name="company_settings"
                value={company}
                onChange={(e) => setCompany(Number(e.target.value))}
              >
                <option value={0}>All companies</option>
                {shell.data.companies.map((row) => (
                  <option key={row.ID} value={row.ID}>
                    {row.name}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>
      </div>

      <div className="list-templates">
        {list.error ? (
          <LoadFailed what="The templates" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : templates.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No templates.
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id}>
              <section className="settings">
                {/* Titlul sablonului sta INAUNTRUL cardului: `body.od-console
                    h4:not([class])` scrie titlurile de ecran cu serif de 22px,
                    iar foaia isi face singura exceptia pentru `section.settings
                    h4`. Afara, fiecare sablon avea un titlu cat numele
                    ecranului de deasupra lui. */}
                <h4>
                  <strong>{template.title}</strong>
                </h4>
                <div className="row">
                  <div className="col-md-10" style={{ wordWrap: 'break-word' }}>
                    <div>{excerpt(template.message)}</div>
                    <a
                      href="#"
                      className="preview-template color-blue"
                      onClick={(e) => {
                        e.preventDefault()
                        setPreview(template.message)
                      }}
                    >
                      <i className="fas fa-search-plus" aria-hidden="true"></i> Preview message body
                    </a>
                  </div>
                  <div className="col-md-2 aright">
                    <button className="btn btn-xs btn-primary" type="button" onClick={() => setEdited(template)}>
                      Edit
                    </button>{' '}
                    <button
                      className="btn btn-xs btn-danger remove-message"
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(template.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <hr className="dotted" />
              </section>
            </div>
          ))
        )}
      </div>

      <TemplateModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={reload}
        companies={shell.data.companies}
      />
      <TemplateModal
        template={edited ?? undefined}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
        companies={shell.data.companies}
      />

      <Modal
        id="previewModal"
        labelledBy="previewModalLabel"
        open={preview !== null}
        onClose={() => setPreview(null)}
        title="Message body"
      >
        <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{preview ?? ''}</div>
      </Modal>
    </div>
  )
}
